'use client';

import React, { useCallback } from 'react';

import { Button } from '@/components/ui/button';

import { COPY } from '@/shared/constants/copies';
import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { useIsWorkspaceAdmin } from '@/shared/hooks/use-is-workspace-admin';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';
import { getUsageDestination } from '@/shared/utils/pricing';
import { openUrlInNewTab } from '@/shared/utils/window';

import { cn } from '@/lib/utils';

import { INLINE_ACTION_CLASS } from './copy';

/** Admins only: everyone else sees usage locked on the Billing tab. */
export const ShowUsageButton = React.memo(
	({
		source,
		feature = MIXPANEL_APP_FEATURES.FORM,
		pageId,
		variant = 'button',
		className,
	}: {
		source: string;
		feature?: MIXPANEL_APP_FEATURES;
		pageId?: string | null;
		/** `link` for one-line nudges where a button would crowd the copy. */
		variant?: 'button' | 'link';
		className?: string;
	}) => {
		const isAdmin = useIsWorkspaceAdmin();

		const handleClick = useCallback(() => {
			emitMixpanelEvent(mixpanelEvents.CLICKED_SHOW_USAGE, { feature, source });
			openUrlInNewTab(getUsageDestination(pageId));
		}, [feature, source, pageId]);

		if (!isAdmin) {
			return null;
		}

		if (variant === 'link') {
			return (
				<button
					type="button"
					onClick={handleClick}
					className={cn(INLINE_ACTION_CLASS, className)}
				>
					{COPY.billing.showUsageCta}
				</button>
			);
		}

		return (
			<Button
				variant="amberOutline"
				size="sm"
				className={cn('flex-shrink-0 text-xs', className)}
				onClick={handleClick}
			>
				{COPY.billing.showUsageCta}
			</Button>
		);
	}
);

ShowUsageButton.displayName = 'ShowUsageButton';
